// src/services/clerkService.js
/**
 * Clerk Authentication Service
 * Manages authentication state and provides auth utilities
 */

import { Clerk } from '@clerk/clerk-js';

export class ClerkService {
  constructor() {
    this.clerk = null;
    this.initialized = false;
  }


  /**
   * Initialize Clerk with publishable key
   */
  async initialize() {
    if (this.initialized) return this.clerk;
    
    // Get publishable key from environment
    console.log('All env vars:', import.meta.env);
    console.log('Looking for VITE_CLERK_PUBLISHABLE_KEY:', import.meta.env?.VITE_CLERK_PUBLISHABLE_KEY);
    
    const clerkPubKey = import.meta.env?.VITE_CLERK_PUBLISHABLE_KEY;
    
    if (!clerkPubKey) {
      console.error('Missing VITE_CLERK_PUBLISHABLE_KEY in environment');
      console.error('Available env vars:', Object.keys(import.meta.env || {}));
      throw new Error('Clerk publishable key not configured');
    }

    // Initialize Clerk with appearance configuration
    this.clerk = new Clerk(clerkPubKey, {
      appearance: {
        layout: {
          logoImageUrl: '/fearn_logo.svg'
        },
        elements: {
          formFieldInput: {
            fontSize: '16px'
          },
          identifierPreview: {
            fontSize: '16px'
          },
          formFieldLabel: {
            fontSize: '14px'
          },
          formButtonPrimary: {
            fontSize: '16px'
          }
        }
      }
    });
    await this.clerk.load();
    this.initialized = true;

    // SECURITY: Clean sensitive Clerk parameters from URL after authentication
    // Prevents session tokens from appearing in browser history and logs
    this.cleanUrlAfterAuth();

    // Make globally available for debugging
    window.clerk = this.clerk;

    console.log('Clerk initialized successfully');
    return this.clerk;
  }

  /**
   * Clean sensitive Clerk authentication parameters from URL
   * Prevents session tokens from being exposed in browser history and server logs
   */
  cleanUrlAfterAuth() {
    const url = new URL(window.location.href);
    const params = url.searchParams;

    // List of sensitive Clerk parameters to remove
    const sensitiveParams = [
      '_clerk_session_id',
      '__clerk_db_jwt',
      '__clerk_status',
      '__clerk_created_session'
    ];

    let cleaned = false;
    sensitiveParams.forEach(param => {
      if (params.has(param)) {
        params.delete(param);
        cleaned = true;
      }
    });

    // If we removed any parameters, update the URL without page reload
    if (cleaned) {
      const cleanUrl = url.pathname + (params.toString() ? '?' + params.toString() : '');
      window.history.replaceState({}, document.title, cleanUrl);
      console.log('Cleaned sensitive authentication parameters from URL');
    }
  }

  /**
   * Get current session token for API calls
   */
  async getToken() {
    if (!this.clerk?.session) {
      console.log('No Clerk session available for token');
      return null;
    }

    // Get the actual JWT token that the backend expects
    const token = await this.clerk.session.getToken();
    // SECURITY: Never log token details - tokens are sensitive credentials
    return token;
  }

  /**
   * Check if user is signed in
   */
  isSignedIn() {
    return !!this.clerk?.user;
  }

  /**
   * Get current user
   */
  getUser() {
    return this.clerk?.user;
  }

  /**
   * Get user metadata
   */
  getUserMetadata() {
    const user = this.getUser();
    if (!user) return null;
    
    return {
      id: user.id,
      email: user.primaryEmailAddress?.emailAddress,
      firstName: user.firstName,
      lastName: user.lastName,
      fullName: user.fullName,
      imageUrl: user.profileImageUrl,
      role: user.publicMetadata?.role || 'user'
    };
  }

  /**
   * Open sign-in modal
   */
  async signIn() {
    if (!this.clerk) {
      throw new Error('Clerk not initialized');
    }
    
    return this.clerk.openSignIn({
      afterSignInUrl: window.location.href,
      afterSignUpUrl: window.location.href,
      appearance: {
        layout: {
          logoImageUrl: '/fearn_logo.svg'
        },
        elements: {
          formFieldInput: {
            fontSize: '16px'
          },
          identifierPreview: {
            fontSize: '16px'
          },
          formFieldLabel: {
            fontSize: '14px'
          },
          formButtonPrimary: {
            fontSize: '16px'
          }
        }
      }
    });
  }

  /**
   * Sign out user
   * SECURITY: Properly invalidates session on both backend and Clerk
   */
  async signOut() {
    if (!this.clerk) return;

    try {
      // SECURITY: Call backend logout FIRST to invalidate server-side session
      // This ensures the session is revoked even if something fails during Clerk signout
      const token = await this.getToken();
      if (token) {
        const apiUrl = import.meta.env?.VITE_API_URL || 'http://localhost:5001';
        try {
          await fetch(`${apiUrl}/api/auth/logout`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });
          console.log('Backend session invalidated successfully');
        } catch (backendError) {
          // Log but continue with Clerk signout - we still want to sign out
          console.error('Failed to invalidate backend session:', backendError);
        }
      }

      // Now invalidate Clerk session
      await this.clerk.signOut();

    } catch (error) {
      console.error('Error during sign out:', error);
      // Still try to redirect even if there was an error
    }

    // Redirect to home page after sign out
    window.location.href = '/';
  }

  /**
   * Mount user button in a container
   */
  mountUserButton(container) {
    if (!this.clerk || !container) return;
    
    this.clerk.mountUserButton(container, {
      appearance: {
        elements: {
          userButtonBox: 'clerk-user-button',
          userButtonTrigger: 'clerk-user-button-trigger'
        }
      }
    });
  }

  /**
   * Listen for auth state changes
   */
  onAuthChange(callback) {
    if (!this.clerk) return;
    
    return this.clerk.addListener(callback);
  }
}

// Export singleton instance
export const clerkService = new ClerkService();